import { useEffect, useMemo, useState } from "react";
import { CircularProgress, Paper, Stack, Typography } from "@mui/material";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import PercentRounded from "@mui/icons-material/PercentRounded";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import AgenteContabilidadeShell from "../components/AgenteContabilidadeShell";
import ChartCard from "../components/ChartCard";
import EmpreendimentosIntersecaoNotice from "../components/EmpreendimentosIntersecaoNotice";
import FilterSelect from "../components/FilterSelect";
import KpiGrid from "../components/KpiGrid";
import MonthFallbackNotice from "../components/MonthFallbackNotice";
import PeriodSelector from "../components/PeriodSelector";
import { useAgenteContabilidadeStore } from "../hooks/useAgenteContabilidadeStore";
import { usePageEmpresaSelection } from "../hooks/usePageEmpresaSelection";
import { usePageMonthSelection } from "../hooks/usePageMonthSelection";
import { logContabilidadeApiError, obterVendas } from "../services/agenteContabilidadeApiService";
import {
  buildEmpresaFilterParams,
  buildEmpreendimentoOptions,
  findEmpreendimentoByFilterValue,
  isTodosEmpreendimentos,
  normalizeEmpreendimentoContext,
  TODOS_EMPREENDIMENTOS_VALUE,
} from "../utils/empreendimentoSelector";
import {
  consolidateTimelineByMonth,
  getIntersecaoForMonth,
  normalizeIntersecaoList,
  normalizeIntersecaoPorMes,
  resolveActiveMonth,
} from "../utils/intersecaoEmpreendimentos";
import { colors } from "../constants/designTokens";
import { formatCurrency, formatPercent } from "../utils/formatters";

const getFirstValue = (item, keys, fallback = 0) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return item[key];
    }
  }

  return fallback;
};

const toNumber = (value) => {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

function normalizeVendasItem(item) {
  return {
    mes_ano: getFirstValue(item, ["mes_ano", "mesAno", "competencia", "mes"], ""),
    idClienteEmpresa: getFirstValue(item, ["idClienteEmpresa", "id_cliente_empresa"], ""),
    nomeEmpresa: getFirstValue(item, ["nomeEmpresa", "nome_empresa"], ""),
    pctVendido: toNumber(getFirstValue(item, ["pctVendido", "percentualVendido", "percentual_vendido"])),
    poc: toNumber(getFirstValue(item, ["poc", "pocExecucao", "poc_execucao"])),
    vgvTotal: toNumber(getFirstValue(item, ["vgvTotal", "vgv_total", "vgvTendencia", "vgv_tendencia"])),
    vgvContratado: toNumber(getFirstValue(item, ["vgvContratado", "vgv_contratado", "vgv"])),
    vgvEstoque: toNumber(getFirstValue(item, ["vgvEstoque", "vgv_estoque", "vgvEmEstoque", "vgv_em_estoque"])),
    receitaApropriada: toNumber(getFirstValue(item, ["receitaApropriada", "receita_apropriada"])),
    receitaAApropriar: toNumber(getFirstValue(item, ["receitaAApropriar", "receita_a_apropriar"])),
    receitaARealizar: toNumber(getFirstValue(item, ["receitaARealizar", "receita_a_realizar"])),
  };
}

function normalizeVendasResponse(payload) {
  if (!payload) {
    return null;
  }

  const sourceTimeline =
    (Array.isArray(payload) && payload) ||
    payload.timeline ||
    payload.grafico ||
    payload.registros ||
    payload.items ||
    payload.data ||
    [];

  if (!Array.isArray(sourceTimeline)) {
    return null;
  }

  const timeline = sourceTimeline.map(normalizeVendasItem).filter((item) => item.mes_ano);
  const months = Array.isArray(payload.meses)
    ? payload.meses
    : Array.isArray(payload.months)
    ? payload.months
    : timeline.map((item) => item.mes_ano);

  return {
    months,
    timeline,
    empreendimentosIntersecao: normalizeIntersecaoList(
      payload.empreendimentosIntersecao ||
        payload.empreendimentos_intersecao ||
        payload.intersecaoEmpreendimentos ||
        payload.intersecao_empreendimentos
    ),
    empreendimentosIntersecaoPorMes: normalizeIntersecaoPorMes(payload),
  };
}

export default function VendasPage() {
  const { state, actions } = useAgenteContabilidadeStore();
  const [selectedMonth, setSelectedMonth] = usePageMonthSelection("vendas");
  const [apiAvailableMonths, setApiAvailableMonths] = useState([]);
  const [apiVendasData, setApiVendasData] = useState(null);
  const [apiStatus, setApiStatus] = useState("idle");

  const backendEmpreendimentos = useMemo(
    () => state.backendCatalog?.empreendimentos || [],
    [state.backendCatalog?.empreendimentos]
  );
  const backendCatalogSyncKey = state.backendCatalog?.lastSyncAt || "";
  const hasBackendEmpreendimentos = backendEmpreendimentos.length > 0;
  const backendEmpresaOptions = useMemo(
    () => buildEmpreendimentoOptions(backendEmpreendimentos),
    [backendEmpreendimentos]
  );
  const [selectedBackendEmpresa, setSelectedBackendEmpresa] = usePageEmpresaSelection("vendas", backendEmpresaOptions);

  useEffect(() => {
    actions.refreshBackendCatalog().catch(() => {});
  }, [actions]);

  useEffect(() => {
    let active = true;

    async function fetchVendas() {
      if (!backendCatalogSyncKey && !selectedBackendEmpresa) {
        setApiStatus("loading");
        return;
      }

      if (hasBackendEmpreendimentos && !selectedBackendEmpresa) {
        setApiStatus("loading");
        return;
      }

      setApiStatus("loading");

      try {
        const params = buildEmpresaFilterParams(selectedBackendEmpresa);
        const payload = await obterVendas(params);
        const normalizedData = normalizeVendasResponse(payload);

        if (!active) return;

        if (normalizedData?.timeline?.length) {
          setApiAvailableMonths(normalizedData.months);
          setApiVendasData(normalizedData);
          setApiStatus("success");
        } else {
          setApiVendasData(null);
          setApiStatus("empty");
        }
      } catch (error) {
        if (!active) return;
        logContabilidadeApiError("vendas", error);
        setApiStatus("error");
      }
    }

    fetchVendas();

    return () => {
      active = false;
    };
  }, [backendCatalogSyncKey, hasBackendEmpreendimentos, selectedBackendEmpresa]);

  const usingApiData = Boolean(apiVendasData?.timeline?.length);
  const vendasData = usingApiData ? apiVendasData : { months: [], timeline: [] };
  const { timeline } = vendasData;
  const isTodos = isTodosEmpreendimentos(selectedBackendEmpresa);
  const visibleTimeline = useMemo(
    () => (isTodos ? consolidateTimelineByMonth(timeline, ["pctVendido", "poc"]) : timeline),
    [isTodos, timeline]
  );
  const months = usingApiData && apiAvailableMonths.length
    ? apiAvailableMonths
    : vendasData.months;

  const activeMonth = resolveActiveMonth(months, selectedMonth);

  const currentSnapshot = visibleTimeline.find((item) => item.mes_ano === activeMonth) || visibleTimeline[visibleTimeline.length - 1];
  const intersecaoEmpreendimentos = getIntersecaoForMonth(vendasData, activeMonth);
  const selectedEmpreendimentoContext = isTodos
    ? { filtro: TODOS_EMPREENDIMENTOS_VALUE, nomeEmpresa: "Todos Empreendimentos" }
    : normalizeEmpreendimentoContext(findEmpreendimentoByFilterValue(backendEmpreendimentos, selectedBackendEmpresa));
  const agenteSerieMensal = visibleTimeline.slice(-36).map((item) => ({
    mesAno: item.mes_ano,
    percentualVendido: item.pctVendido,
    poc: item.poc,
    vgvTotal: item.vgvTotal,
    vgvContratado: item.vgvContratado,
    vgvEstoque: item.vgvEstoque,
    receitaApropriada: item.receitaApropriada,
    receitaAApropriar: item.receitaAApropriar,
    receitaARealizar: item.receitaARealizar,
  }));

  const empreendimentoOptions = backendEmpresaOptions;
  const selectedEmpreendimentoValue = selectedBackendEmpresa;

  function handleEmpreendimentoChange(value) {
    setSelectedMonth((current) => current || activeMonth);
    setSelectedBackendEmpresa(value || "");
    setApiAvailableMonths([]);
  }

  const metrics = currentSnapshot
    ? [
        { label: "% Vendido", value: formatPercent(currentSnapshot.pctVendido, { decimals: 2 }), variant: "purple", icon: PercentRounded },
        { label: "% POC - Percentage of Completion", value: formatPercent(currentSnapshot.poc, { decimals: 2 }), variant: "blue", icon: TrendingUpRounded },
        { label: "VGV Tendência", value: formatCurrency(currentSnapshot.vgvTotal), variant: "green", icon: AttachMoneyRounded },
        { label: "VGV Contratado", value: formatCurrency(currentSnapshot.vgvContratado), variant: "magenta", icon: TrendingUpRounded },
        { label: "VGV em Estoque", value: formatCurrency(currentSnapshot.vgvEstoque), variant: "navy", icon: Inventory2Outlined },
        { label: "Receita Apropriada", value: formatCurrency(currentSnapshot.receitaApropriada), variant: "orange", icon: ReceiptLongOutlined },
        { label: "Receita a Apropriar", value: formatCurrency(currentSnapshot.receitaAApropriar), variant: "teal", icon: CalendarMonthOutlined },
        { label: "Receita a Realizar", value: formatCurrency(currentSnapshot.receitaARealizar), variant: "red", icon: BarChartRounded },
      ]
    : [];

  const chartData = visibleTimeline.map((item) => ({
    mes_ano: item.mes_ano,
    vgvContratado: item.vgvContratado / 1000000,
    receitaApropriada: item.receitaApropriada / 1000000,
    receitaAApropriar: item.receitaAApropriar / 1000000,
  }));
  const isLoadingVendas = (apiStatus === "idle" || apiStatus === "loading") && !usingApiData;

  return (
    <AgenteContabilidadeShell
      title="Receita de Vendas"
      subtitle="Análise mensal das receitas de vendas"
      updating={apiStatus === "loading" && usingApiData}
      agenteContext={{
        paginaAtual: "vendas",
        empresa: selectedBackendEmpresa,
        competencia: activeMonth,
        dadosVisiveis: currentSnapshot
          ? {
              percentualVendido: currentSnapshot.pctVendido,
              poc: currentSnapshot.poc,
              vgvTotal: currentSnapshot.vgvTotal,
              vgvContratado: currentSnapshot.vgvContratado,
              vgvEstoque: currentSnapshot.vgvEstoque,
              receitaApropriada: currentSnapshot.receitaApropriada,
              receitaAApropriar: currentSnapshot.receitaAApropriar,
              receitaARealizar: currentSnapshot.receitaARealizar,
              empresaSelecionada: selectedEmpreendimentoContext,
              serieMensal: agenteSerieMensal,
            }
          : {},
      }}
      actions={[
        <FilterSelect
          key="emp"
          value={selectedEmpreendimentoValue}
          onChange={handleEmpreendimentoChange}
          options={empreendimentoOptions}
          icon={BusinessRounded}
          minWidth={286}
        />,
      ]}
    >
      {isLoadingVendas ? (
        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
          <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center" }}>
            <CircularProgress size={34} />
            <Typography sx={{ fontWeight: 900, color: colors.text }}>
              Carregando dados de vendas
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 13 }}>
              Aguarde enquanto consultamos os indicadores calculados pela API.
            </Typography>
          </Stack>
        </Paper>
      ) : !visibleTimeline.length ? (
        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
          <Stack alignItems="center" spacing={1.2} sx={{ textAlign: "center" }}>
            <Typography sx={{ fontWeight: 900, color: colors.text, fontSize: 20 }}>
              Nenhum dado disponível
            </Typography>
            <Typography sx={{ color: colors.muted, fontSize: 14, maxWidth: 520 }}>
              Não encontramos dados disponíveis para exibição no momento.
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <>
          <PeriodSelector months={months} value={activeMonth} onChange={setSelectedMonth} />
          <MonthFallbackNotice requestedMonth={selectedMonth} activeMonth={activeMonth} />
          <EmpreendimentosIntersecaoNotice
            isTodosEmpreendimentos={isTodos}
            activeMonth={activeMonth}
            empreendimentos={intersecaoEmpreendimentos}
          />
          <KpiGrid metrics={metrics} />
          <ChartCard
            title="Evolução de Vendas"
            data={chartData}
            series={[
              { key: "vgvContratado", name: "VGV Contratado", color: "#6366f1" },
              { key: "receitaApropriada", name: "Receita Apropriada", color: "#f59e0b" },
              { key: "receitaAApropriar", name: "Receita a Apropriar", color: "#06b6d4" },
            ]}
            headerGradient="purple"
          />
        </>
      )}
    </AgenteContabilidadeShell>
  );
}
